import { EApplicantType, EFinancingProgram, EProvince } from '@if/core/types'
import { Box, Divider, Typography } from '@mui/material'
import {
  Control,
  FieldError,
  FieldErrors,
  FieldErrorsImpl,
  Merge,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Applicant, CreditApplication, FinancingProgramConfig } from '../../../../../../data/types'
import AddressComponent from './components/AddressComponent'
import ApplicantExpense from './components/applicantExpenseComponent'
import ApplicantIncomeSection from './components/applicantIncomeComponent'
import ApplicantInformationParameters from './components/applicantInformationComponent'

interface Props {
  register: UseFormRegister<CreditApplication>
  watch: UseFormWatch<CreditApplication>
  setValue: UseFormSetValue<CreditApplication>
  getValues: UseFormGetValues<CreditApplication>
  trigger: UseFormTrigger<CreditApplication>
  errors: Merge<FieldError, FieldErrorsImpl<Applicant>> | undefined
  applicantType: EApplicantType
  formControl: Control<CreditApplication>
  editDisabled: boolean
  financingProgramId: EFinancingProgram
  financingProgramConfig: FinancingProgramConfig
}

const ApplicantParameters = ({
  register,
  watch,
  setValue,
  getValues,
  trigger,
  errors,
  applicantType: name,
  formControl,
  editDisabled,
  financingProgramId,
  financingProgramConfig,
}: Props) => {
  const { t } = useTranslation()
  const applicantProvince = watch(`${name}.currentAddress.stateIso`)
  const useExpenseIncomeSections =
    financingProgramId !== EFinancingProgram.Products ||
    (financingProgramId === EFinancingProgram.Products && applicantProvince === EProvince.Quebec)
  const typedErrors = errors as FieldErrors<Applicant>
  return (
    <>
      <ApplicantInformationParameters
        register={register}
        getValues={getValues}
        trigger={trigger}
        errors={typedErrors}
        name={name}
        formControl={formControl}
        editDisabled={editDisabled}
      />
      <Box
        sx={{
          height: 20,
        }}
      />
      <Divider>
        <Typography variant="h4" component="span" gutterBottom>
          {t('editCreditApplication.address.label')}
        </Typography>
      </Divider>
      <AddressComponent
        formControl={formControl}
        register={register}
        errors={typedErrors?.currentAddress}
        editDisabled={editDisabled}
        name={`${name}.currentAddress`}
      />
      {financingProgramConfig.useExpenseSection && useExpenseIncomeSections && (
        <>
          <Divider>
            <Typography variant="h4" component="span" gutterBottom>
              {t('editCreditApplication.expenses.label')}
            </Typography>
          </Divider>
          <ApplicantExpense
            register={register}
            watch={watch}
            setValue={setValue}
            errors={typedErrors?.expenses}
            name={`${name}.expenses`}
            showDetailedForm={false}
            financingProgramId={financingProgramId}
            editDisabled={editDisabled}
          />
        </>
      )}
      {financingProgramConfig.useIncomeSection && useExpenseIncomeSections && (
        <ApplicantIncomeSection
          register={register}
          errors={typedErrors?.otherIncomes}
          currentJobsErrors={typedErrors?.currentJobs}
          trigger={trigger}
          formControl={formControl}
          name={name}
          editDisabled={editDisabled}
          financingProgramId={financingProgramId}
        />
      )}
    </>
  )
}

export default ApplicantParameters
